import React, { useState, useEffect } from "react";
import "./App.css";
import searchContacts from "./components/searchContacts";
import startCall from "./components/startCall";
import RoutingConfig from "./components/RoutingConfig";
import favicon from "./favicon.ico";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";

const App = () => {
  const [contactId, setContactId] = useState("");
  const [searchResults, setSearchResults] = useState(null);
  const [extension, setExtension] = useState("");
  const [callResult, setCallResult] = useState("");
  const [callResultType, setCallResultType] = useState("");
  const [loadingContactSearch, setLoadingContactSearch] = useState(false);
  const [loadingInitiateCall, setLoadingInitiateCall] = useState(false);
  const [contactIdError, setContactIdError] = useState("");
  const [extensionError, setExtensionError] = useState("");
  const [callButtonDisabled, setCallButtonDisabled] = useState(false);
  const [showCallSection, setShowCallSection] = useState(false);
  const [savedExtension, setSavedExtension] = useState("");
  const [isDarkTheme, setIsDarkTheme] = useState(false);

  // Add a function to clear all error and result messages
  const clearAllMessages = () => {
    setCallResult("");
    setCallResultType("");
    setContactIdError("");
    setExtensionError("");
  };

  // Modify useEffect to clear messages on initial load
  useEffect(() => {
    const savedExt = localStorage.getItem("savedExtension");
    const lastContact = localStorage.getItem("lastContact");
    const savedTheme = localStorage.getItem("theme");
    
    // Clear any stale messages on component mount
    clearAllMessages();
    
    if (savedExt) {
      setExtension(savedExt);
      setSavedExtension(savedExt);
    }
    
    if (lastContact) {
      try {
        const contactData = JSON.parse(lastContact);
        setSearchResults(contactData);
        setShowCallSection(true);
        setContactId(contactData.id);
      } catch (error) {
        console.error("Error loading last contact:", error);
      }
    }

    if (savedTheme === "dark") {
      setIsDarkTheme(true);
      document.documentElement.setAttribute("data-theme", "dark");
    }
  }, []);

  const toggleTheme = () => {
    const newTheme = !isDarkTheme;
    setIsDarkTheme(newTheme);
    if (newTheme) {
      document.documentElement.setAttribute("data-theme", "dark");
      localStorage.setItem("theme", "dark");
    } else {
      document.documentElement.removeAttribute("data-theme");
      localStorage.setItem("theme", "light");
    }
  };

  const handleExtensionChange = (e) => {
    const value = e.target.value;
    setExtension(value);
    setExtensionError("");
  };

  const handleSaveExtension = () => {
    if (!extension) {
      setExtensionError("Please enter an extension");
      return;
    }

    if (!/^\d+$/.test(extension)) {
      setExtensionError("Extension must contain only numbers");
      return;
    }

    localStorage.setItem("savedExtension", extension);
    setSavedExtension(extension);
    setExtensionError("");
  };

  const handleContactIdSearch = async () => {
    if (!contactId) {
      setContactIdError("Please enter a contact ID");
      return;
    }

    if (!extension) {
      setExtensionError("Please enter and save your extension first");
      return;
    }

    setLoadingContactSearch(true);
    setContactIdError("");
    setSearchResults(null);
    setShowCallSection(false);
    setCallResult("");
    setCallResultType("");

    try {
      const result = await searchContacts(contactId);
      if (result && result.id) {
        setSearchResults(result);
        setShowCallSection(true);
        // Save the contact data to localStorage
        localStorage.setItem("lastContact", JSON.stringify(result));
      } else {
        setContactIdError("No contact found with this ID");
      }
    } catch (error) {
      console.error("Error searching contacts:", error);
      setContactIdError("An error occurred while searching. Please try again.");
    } finally {
      setLoadingContactSearch(false);
    }
  };

  const handleInitiateCall = async () => {
    if (!extension) {
      setExtensionError("Please enter an extension");
      return;
    }

    if (!/^\d+$/.test(extension)) {
      setExtensionError("Extension must contain only numbers");
      return;
    }

    if (!searchResults || !searchResults.properties) {
      setCallResult("No contact data available");
      setCallResultType("error");
      return;
    }

    const phone = searchResults.properties.phone || 
                 searchResults.properties.alt_number || 
                 searchResults.properties.call_number;

    if (!phone) {
      setCallResult("No phone number found for this contact");
      setCallResultType("error");
      return;
    }

    // Clear any previous error or success messages
    clearAllMessages();
    
    setLoadingInitiateCall(true);
    setCallButtonDisabled(true);

    try {
      const result = await startCall(searchResults.id, phone, extension);
      setCallResult(result);
      setCallResultType("success");
      
      // Reset the form after successful call
      setTimeout(() => {
        setCallResult("");
        setCallResultType("");
        setCallButtonDisabled(false);
      }, 3000);
    } catch (error) {
      console.error("Error initiating call:", error);
      
      // Improved error handling
      let errorMessage = "Failed to initiate call";
      
      if (error.response) {
        // The server responded with an error status
        if (error.response.status === 401 || error.response.status === 403) {
          errorMessage = "You've been logged out. Please refresh the page and try again.";
        } else if (error.response.data && error.response.data.error) {
          errorMessage = error.response.data.error;
        } else {
          errorMessage = `Server error: ${error.response.status}`;
        }
      } else if (error.request) {
        // The request was made but no response was received
        errorMessage = "No response from server. Please check your connection.";
      } else if (error.message) {
        // Something happened in setting up the request
        errorMessage = error.message;
      }
      
      setCallResult(errorMessage);
      setCallResultType("error");
      setCallButtonDisabled(false);
    } finally {
      setLoadingInitiateCall(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      if (showCallSection) {
        handleInitiateCall();
      } else {
        handleContactIdSearch();
      }
    }
  };

  return (
    <Router>
      <div className="App">
        <div className="theme-switch">
          <span style={{ color: isDarkTheme ? '#fff' : '#333' }}>
            {isDarkTheme ? '🌙' : '☀️'}
          </span>
          <button 
            className="theme-switch-button"
            onClick={toggleTheme}
            aria-label="Toggle theme"
          />
        </div>
        <header className="App-header">
          <h1 className="app-title">
            <img
              src={favicon}
              alt="icon"
              style={{
                width: "1.2em",
                height: "1.2em",
                marginRight: "0.5rem"
              }}
            />
            VoisHub
          </h1>

          <div className="middle-container">
            <Routes>
              <Route path="/" element={
                <div className="contact-search">
                  <div className="extension-settings" style={{ 
                    backgroundColor: isDarkTheme ? '#222' : undefined
                  }}>
                    <h3>Extension Settings</h3>
                    <div className="extension-input-container">
                      <input
                        type="number"
                        placeholder="Enter your extension"
                        value={extension}
                        onChange={handleExtensionChange}
                        onKeyPress={handleKeyPress}
                        className="search-input"
                        min="0"
                        style={{ 
                          backgroundColor: isDarkTheme ? '#333' : undefined,
                          color: isDarkTheme ? '#e0e0e0' : undefined
                        }}
                      />
                      <button
                        onClick={handleSaveExtension}
                        className="save-extension-button"
                        disabled={extension === savedExtension}
                      >
                        Save
                      </button>
                    </div>
                    {extensionError && <p className="error">{extensionError}</p>}
                    {savedExtension && (
                      <p className="success">Saved extension: {savedExtension}</p>
                    )}
                  </div>

                  <h3 className="contact-search-header">Contact Search</h3>
                  <div>
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'row',
                      alignItems: 'center',
                      gap: '4px'
                    }}>
                      <input
                        type="number"
                        placeholder="Enter contact ID"
                        value={contactId}
                        onChange={(e) => {
                          setContactIdError("");
                          setContactId(e.target.value);
                        }}
                        onKeyPress={handleKeyPress}
                        className="search-input"
                        min="0"
                        style={{ 
                          borderRadius: '4px',
                          backgroundColor: isDarkTheme ? '#333' : undefined,
                          color: isDarkTheme ? '#e0e0e0' : undefined
                        }}
                      />
                      <button
                        onClick={handleContactIdSearch}
                        className="search-button"
                        disabled={loadingContactSearch || !extension}
                        style={{ 
                          borderRadius: '4px',
                          width: 'auto',
                          minWidth: '70px'
                        }}
                      >
                        {loadingContactSearch ? (
                          <i className="fa fa-spinner fa-spin"></i>
                        ) : (
                          "Search"
                        )}
                      </button>
                    </div>
                    {contactIdError && <p className="error">{contactIdError}</p>}
                  </div>

                  {searchResults && (
                    <div className="search-results" style={{
                      backgroundColor: isDarkTheme ? '#222' : undefined
                    }}>
                      <h3>Contact Details</h3>
                      <p>Contact ID: {searchResults.id}</p>
                      <p>First name: {searchResults.properties?.firstname || "N/A"}</p>
                      <p>Last name: {searchResults.properties?.lastname || "N/A"}</p>
                      <p>Email: {searchResults.properties?.email || "N/A"}</p>
                      <p>Country: {searchResults.properties?.country || "N/A"}</p>
                    </div>
                  )}

                  {showCallSection && (
                    <div className="call-button-container">
                      <button
                        onClick={handleInitiateCall}
                        className="call-button"
                        disabled={loadingInitiateCall || callButtonDisabled}
                      >
                        {loadingInitiateCall ? (
                          <i className="fa fa-spinner fa-spin"></i>
                        ) : (
                          <>
                            <i className="fa fa-phone"></i>
                            Call
                          </>
                        )}
                      </button>
                    </div>
                  )}

                  {callResult && (
                    <p className={callResultType === "error" ? "error" : "success"}>
                      {callResult}
                    </p>
                  )}
                </div>
              } />
              <Route path="/arikswoops" element={<RoutingConfig />} />
            </Routes>
          </div>
        </header>
      </div>
    </Router>
  );
};

export default App;
